import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { seo as api, explainError, type AuditDupGroup, type AuditGroup, type AuditRow, type SeoAudit } from '../lib/api'
import { useToast } from '../lib/toast'
import { PageHeader } from '../components/ui/PageHeader'
import { Card } from '../components/ui/Card'
import { Badge } from '../components/ui/Badge'
import { Button } from '../components/ui/Button'
import { Spinner } from '../components/ui/Spinner'

/**
 * Danh mục không có route riêng (form nằm trong modal của cây danh mục), nên trỏ về
 * `/categories?edit=<id>` — `CategoryTree` đọc param đó và mở sẵn modal.
 *
 * ⚠️ **Phải chịu được `type` lạ.** Ngày 2026-09-11 trang này trả về **màn hình trắng
 * hoàn toàn**: API thêm loại `page` ở đợt trước mà `AuditRow['type']` phía admin không
 * được cập nhật, nên `EDIT_HREF['page']` là `undefined` và gọi nó ném `TypeError` — hạ cả
 * SPA. `tsc` không bắt được vì kiểu khai báo **nói dối về dữ liệu thật**, và lời nói dối
 * đó nhất quán với chính nó.
 *
 * Dùng `Partial` + giá trị dự phòng thay vì `Record` đủ khoá: một loại nội dung mới xuất
 * hiện thì tệ nhất là dòng đó không bấm được, chứ không phải cả trang biến mất.
 */
const EDIT_HREF: Partial<Record<string, (id: string) => string>> = {
  product: (id) => `/products/${id}`,
  post: (id) => `/posts/${id}`,
  category: (id) => `/categories?edit=${id}`,
}
const TYPE_LABEL: Partial<Record<string, string>> = { product: 'SP', post: 'Bài', category: 'DM' }
const editHref = (r: AuditRow) => EDIT_HREF[r.type]?.(r.id) ?? '/seo'
const typeLabel = (r: AuditRow) => TYPE_LABEL[r.type] ?? r.type

function Rows({ items }: { items: AuditRow[] }) {
  return (
    <ul className="mt-1.5 space-y-0.5 text-xs">
      {items.map((r) => (
        <li key={`${r.type}-${r.id}`} className="flex items-center gap-2">
          <span className="shrink-0 text-2xs uppercase text-ink-faint">{typeLabel(r)}</span>
          <Link to={editHref(r)} className="truncate text-ink hover:underline">{r.title}</Link>
        </li>
      ))}
    </ul>
  )
}

function Issue({ g, title, why, tone = 'warning' }: { g: AuditGroup; title: string; why: string; tone?: 'warning' | 'danger' }) {
  return (
    <div className="border-b border-border py-3 last:border-0">
      <div className="flex items-center gap-2">
        {g.count ? <Badge tone={tone}>{g.count}</Badge> : <Badge tone="success">0</Badge>}
        <span className="text-sm text-ink">{title}</span>
      </div>
      <p className="mt-1 text-xs text-ink-muted">{why}</p>
      {g.count > 0 && (
        <>
          <Rows items={g.items} />
          {g.count > g.items.length && <p className="mt-1 text-2xs text-ink-faint">… và {g.count - g.items.length} trang khác</p>}
        </>
      )}
    </div>
  )
}

function Duplicates({ g, title, why }: { g: AuditDupGroup; title: string; why: string }) {
  return (
    <div className="border-b border-border py-3 last:border-0">
      <div className="flex items-center gap-2">
        {g.count ? <Badge tone="danger">{g.count}</Badge> : <Badge tone="success">0</Badge>}
        <span className="text-sm text-ink">{title}</span>
      </div>
      <p className="mt-1 text-xs text-ink-muted">{why}</p>
      {g.groups.map((grp) => (
        <div key={grp.value} className="mt-2 rounded-card-sm bg-sand p-2">
          <p className="truncate font-mono text-2xs text-ink-muted">"{grp.value}"</p>
          <Rows items={grp.items} />
        </div>
      ))}
    </div>
  )
}

/**
 * Rà soát SEO toàn site — những vấn đề **chỉ thấy được khi nhìn tổng thể**, không thể
 * phát hiện khi đang soạn một trang: tiêu đề trùng, từ khoá bị hai trang cùng nhắm
 * (cannibalization), trang publish mà thiếu meta, nội dung mỏng.
 *
 * Chỉ tính document **đang hiển thị trên web** — bản nháp chưa lên Google nên chưa phải
 * vấn đề SEO.
 */
export default function SeoHealth() {
  const toast = useToast()
  const [audit, setAudit] = useState<SeoAudit | null>(null)
  const [busy, setBusy] = useState(false)

  const load = useCallback(() => {
    setBusy(true)
    api.audit().then(setAudit).catch((e) => toast.error(explainError(e).message)).finally(() => setBusy(false))
  }, [toast])
  useEffect(() => { load() }, [load])

  if (!audit) return <Spinner />

  const problems =
    audit.missingTitle.count + audit.missingDescription.count + audit.missingKeyword.count +
    audit.missingImage.count + audit.thinContent.count + audit.noindexed.count +
    audit.duplicateTitles.count + audit.duplicateKeywords.count

  return (
    <>
      <PageHeader title="Sức khoẻ SEO" subtitle={`${audit.publishedTotal} trang đang hiển thị trên web`}
        actions={<Button variant="secondary" loading={busy} onClick={load}>Kiểm tra lại</Button>} />

      {audit.siteIndexingBlocked && (
        <p className="mb-4 rounded-card-sm bg-danger-tint px-4 py-3 text-sm text-danger">
          Toàn site đang chặn Google. Vào <Link to="/settings" className="underline">Cài đặt SEO</Link> để bật lại — mọi việc bên dưới vô nghĩa cho tới khi bật.
        </p>
      )}

      <p className={`mb-4 rounded-card-sm px-4 py-3 text-sm ${problems ? 'bg-warning-tint text-warning' : 'bg-success-tint text-success'}`}>
        {problems ? `Có ${problems} nhóm vấn đề cần xử lý.` : 'Không phát hiện vấn đề nào ✓'}
      </p>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card title="Trùng lặp — ưu tiên xử lý trước">
          <Duplicates g={audit.duplicateKeywords} title="Từ khoá chính bị dùng ở nhiều trang"
            why="Hai trang cùng nhắm một từ khoá sẽ tự cạnh tranh nhau (keyword cannibalization); cả hai đều tụt thay vì một trang lên top. Đổi từ khoá một bên, hoặc gộp nội dung lại." />
          <Duplicates g={audit.duplicateTitles} title="Tiêu đề SEO trùng nhau"
            why="Google không biết chọn trang nào và Search Console báo lỗi Duplicate title. Mỗi trang cần một tiêu đề riêng." />
        </Card>

        <Card title="Thiếu thông tin cơ bản">
          <Issue g={audit.missingTitle} title="Chưa đặt tiêu đề SEO"
            why="Không đặt thì web dùng mẫu tiêu đề chung — thường không chứa đúng từ khoá muốn nhắm." />
          <Issue g={audit.missingDescription} title="Chưa có meta description"
            why="Google sẽ tự cắt một đoạn bất kỳ trong nội dung, thường cụt và không có lời kêu gọi hành động." />
          <Issue g={audit.missingKeyword} title="Chưa đặt từ khoá chính"
            why="Không có từ khoá thì không chấm điểm được, và thường là dấu hiệu trang được viết mà không nhắm truy vấn nào cả." />
        </Card>

        <Card title="Nội dung & hình ảnh">
          <Issue g={audit.missingImage} title="Không có ảnh" tone="danger"
            why="Sản phẩm không ảnh gần như không bán được; bài viết không ảnh đại diện thì chia sẻ lên Facebook/Zalo chỉ hiện chữ." />
          <Issue g={audit.thinContent} title="Nội dung mỏng"
            why="Sản phẩm dưới 120 từ và bài viết dưới 400 từ rất khó cạnh tranh. Bổ sung chất liệu, kích thước, cách dùng, ai nên mua." />
        </Card>

        <Card title="Đang bị chặn khỏi Google">
          <Issue g={audit.noindexed} title="Trang đặt noindex" tone="danger"
            why="Những trang này đang hiển thị cho khách nhưng bị chặn khỏi Google. Nếu không cố ý thì tắt noindex ở tab SEO của từng trang." />
        </Card>
      </div>
    </>
  )
}
